import React, { useEffect, useState } from 'react'
import {
  Box,
  Button, Checkbox,
  Container,
  FormControlLabel, FormGroup,
  Grid,
  Paper, Typography,
}                                     from '@mui/material'
import { useTranslation }             from 'react-i18next'
import { useNavigate, useParams }     from 'react-router-dom'
import { useApi }                     from '../../utils/api'
import { useSnackbar }                from 'notistack'
import AlertSimpleDialog              from '../../components/alert-simple-dialog'
import Page                           from '../../components/Page'
import ShortFormControl               from '../../components/short-form-control'
import ShortFormSelect                from '../../components/short-form-select'
import Consts                         from '../../consts'

export default function SharePage() {

  const api = useApi()
  const navigate = useNavigate()
  const { serviceId, shareId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [operators, setOperators] = useState([])
  const [values, setValues] = useState({
    name: '',
    serviceId: serviceId,
    operatorId: '',
    type: '',
    messageType: Consts.Share.messageTypePartnerShare,
    variant: Consts.Share.variantPrice,
    value: '',
    mtValue: '',
    mediatexShare: false,
    position: '',
  })
  const [shownDeleteDialog, setShownDeleteDialog] = useState(false)

  useEffect(() => {
    api.operator.list().then((response) => setOperators(response.data)).catch(err => {
      console.error(err)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    (async () => {
      if (!isNaN(shareId)) {
        try{
          let share = await api.share.detail(shareId)
          setValues({
            ...share,
            value: share.variant === Consts.Share.variantPercent ? share.value * 100 : share.value,
            mtValue: share.variant === Consts.Share.variantPercent && share.mediatexShare ? share.mtValue * 100 : share.mtValue,
          })
        } catch (e){
          enqueueSnackbar(t('Can\'t load share'), {
            variant: "error"
          })
        }
      }
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId])

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value })
  }

  const handleCloseDeleteDialog = (result) => {
    setShownDeleteDialog(false)
    if (result) {
      api.share.delete(shareId).then(() => {
        enqueueSnackbar(t('Service share deleted'), {
          variant: 'success'
        })
        navigate(`/dashboard/services/${serviceId}`)
      }).catch(error => {
        enqueueSnackbar(t('Failed to delete service share'), {
          variant: 'error'
        })
      })
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    let data = {
      ...values,
      serviceId: parseInt(serviceId),
    }
    data.value = data.value ? parseFloat(`${data.value}`.replaceAll(',','.')) : 0
    data.mtValue = data.mtValue ? parseFloat(`${data.mtValue}`.replaceAll(',','.')) : 0
    if (values.variant === Consts.Share.variantPercent) {
      data.value = data.value / 100
      data.mtValue = data.mtValue / 100
    }
    try {
      let share = await api.share.save(data)
      navigate(`/dashboard/services/${serviceId}/share/${share.id}`)
      enqueueSnackbar(t('Share saved'), {
        variant: 'success'
      })
    } catch (e) {
      enqueueSnackbar(t('Failed to save service share'), {
        variant: 'error'
      })
    }
  }

  return (
    <Page title={t('Service share')}>
      <Container component="main">
        <Typography variant="h4" gutterBottom>{t('Share')}</Typography>
        <Grid component={'form'} onSubmit={onSubmit} item xs={12}>
          <Paper sx={{ p: 2, width: '100%' }}>
            <Grid container sx={{ width: '100%' }} spacing={2}>
              <Grid item xs={12}>
                <ShortFormControl name={'name'} title={t('Name')} value={values.name ?? ''} handleChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                {operators.length > 0 && <ShortFormSelect name={'operatorId'} title={t('Operator')} value={values.operatorId} handleChange={handleChange} items={operators.map(it => ({ id: it.id, name: it.name }))} />}
              </Grid>
              <Grid item xs={12} md={4}>
                <ShortFormSelect name={'type'} title={t('Share type')} value={values?.type} handleChange={handleChange} items={[
                  {id: Consts.Share.typePartner, name: t('Partner')},
                  {id: Consts.Share.typeOperator, name: t('Operator')},
                ]} />
              </Grid>
              <Grid item xs={12} md={4}>
                <ShortFormSelect name={'messageType'} title={t('Share message type')} value={values?.messageType} handleChange={handleChange} allowAllOption={false} items={[
                  // {id: Consts.Share.messageTypeVat, name: t('messageType_vat_fee')}, // do not included in options - generated automatically
                  // {id: Consts.Share.messageTypeMediatexFee, name: t('messageType_mediatex_fee')}, // do not included in options - generated automatically
                  // {id: Consts.Share.messageTypeMediatexShare, name: t('messageType_mediatex_share')}, // do not included in options - generated automatically
                  {id: Consts.Share.messageTypeOperatorFee, name: t('messageType_operator_fee')},
                  {id: Consts.Share.messageTypeOperatorShare, name: t('messageType_operator_share')},
                  {id: Consts.Share.messageTypePartnerFee, name: t('messageType_partner_fee')},
                  {id: Consts.Share.messageTypePartnerShare, name: t('messageType_partner_share')},
                ]} />
              </Grid>
              <Grid item xs={12} md={3}>
                <ShortFormControl name={'position'} title={t('Share position')} value={values?.position ?? ''} handleChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={3}>
                <ShortFormSelect name={'variant'} title={t('Share variant')} value={values?.variant} handleChange={handleChange} allowAllOption={false} items={[
                  {id: Consts.Share.variantPrice, name: t('Price share')},
                  {id: Consts.Share.variantPercent, name: t('Percent share')},
                ]} />
              </Grid>
              <Grid item xs={12} md={3}>
                <ShortFormControl name={'value'} title={t('Share value')} value={values?.value ?? ''} handleChange={handleChange} />
                <Typography variant={'caption'}>{values?.variant === Consts.Share.variantPrice ? t(`share-price-hint`) : t('share-percent-hint')}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <ShortFormControl name={'mtValue'} title={t('Share mt value')} value={(!values?.mediatexShare ? 0 : values?.mtValue) ?? ''} handleChange={handleChange} inputProps={{ disabled: !values?.mediatexShare }} />
                <FormGroup>
                  <FormControlLabel control={<Checkbox/>} checked={values?.mediatexShare ?? false} onChange={(e) => {
                    handleChange('mediatexShare')({ target: { value: e.target.checked } })
                  }} label={t('Share mt ratio')}/>
                </FormGroup>
              </Grid>
            </Grid>
          </Paper>
          <Box sx={{ mt: 2, px: 1 }}>
            <Button type={'submit'} variant={'contained'}>{t('Save')}</Button>
            { !isNaN(shareId) && (<Button type={'button'} color={'error'} variant={'contained'} sx={{ ml: 2 }} onClick={() => { setShownDeleteDialog(true) }}>{t('Delete')}</Button>) }
          </Box>
        </Grid>
        <AlertSimpleDialog
          title={t('Deleting share')}
          message={t('delete-confirm-msg', { name: values.id })}
          open={shownDeleteDialog}
          onClose={handleCloseDeleteDialog}
        />
      </Container>
    </Page>
  )
}